import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { DigitalAdviceService } from '@shared/services/digital-advice/digital-advice.service';
import {
  CheckClientEligibility,
  enrollInDA,
  enrollInDASuccess,
  RetakeRiskQuiz,
  rollbackOnboarding,
  UpdatePortfolioMatchResult,
} from '@store/actions/open-choice/open-choice.actions';
import { getMfePersonalAddressState } from '@store/reducers';
import {
  selectAcceptedDAOffer,
  selectDAClientEligibilityBody,
  selectOnboardingInitialized,
  selectOnboardingInitRequestBody,
  selectRequestBodyForOnboardingInitialization,
} from '@store/selectors/open-choice/open-choice.selectors';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  mergeMap,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

import * as fromRootStore from '../../index';
import { closeSpinner, LogInfo } from '../../index';

@Injectable()
export class OpenChoiceEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private loggerService: LoggerService,
    private digitalAdviceService: DigitalAdviceService,
  ) {}

  retakeRiskQuiz$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RetakeRiskQuiz),
      mergeMap(() => [
        UpdatePortfolioMatchResult({ payload: null }),
        fromRootStore.Go({ path: ['secure/open-choice/portfolio-match'] }),
        fromRootStore.LogInfo({
          payload: {
            eventId: 'open-choice-risk-quiz--retake',
            message: 'Client is retaking risk quiz',
          },
        }),
      ]),
    ),
  );

  enrollInDA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enrollInDA),
      withLatestFrom(this.store$.pipe(select(selectRequestBodyForOnboardingInitialization))),
      switchMap(([, clientDetailsRequestBody]) => {
        this.loggerService.info(
          {
            eventId: 'open-choice-effect--client-createEffects--status',
            message: 'Calling Guided Choice middleware call in-progress',
          },
          LoggerCodes.GENERAL_SUCCESS,
        );
        return this.digitalAdviceService.clientEnrollment(clientDetailsRequestBody).pipe(
          switchMap((response) => {
            const actions = [];
            actions.push(
              LogInfo({
                payload: {
                  eventId: 'open-choice-effect--client-DigitalAdviceEffects-status',
                  message: 'Enrollment Success',
                  response,
                },
              }),
            );
            actions.push(enrollInDASuccess());
            actions.push(closeSpinner());
            return actions;
          }),
        );
      }),
      catchError((error) => {
        return [
          fromRootStore.LogError({
            payload: {
              eventId: 'open-choice-effect--client-enrollment--error',
              message: `Error for enrolling client's Digital Advice`,
              error,
            },
          }),
          fromRootStore.rejectDAOffer(),
          fromRootStore.UpdateDAComponentFailure({ payload: true }),
          fromRootStore.Go({ path: ['secure/minor-transfer/confirm'] }),
        ];
      }),
    ),
  );

  clientDigitalAdviceEligibility$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CheckClientEligibility),
      withLatestFrom(this.store$.select(selectDAClientEligibilityBody)),
      switchMap(([, body]) => {
        return this.digitalAdviceService.clientEligibility(body).pipe(
          switchMap(({ eligibility }) => {
            if (eligibility) {
              return [
                // call UpdateDraft to update the draft for investment-objective (/open-choice/offer is happy path)
                // if investment-objective will not call Next(), this won't be dispatched. Explicitely dispatching here.
                fromRootStore.UpdateDraft(),
                fromRootStore.Go({ path: ['secure/open-choice/offer'] }),
                fromRootStore.LogInfo({
                  payload: {
                    eventId: 'open-choice-effect--client-eligibility--eligible',
                    eligibility,
                    message: 'Client is eligible for Digital Advice',
                  },
                }),
              ];
            } else {
              return [
                fromRootStore.Next(),
                fromRootStore.LogInfo({
                  payload: {
                    eventId: 'open-choice-effect--client-eligibility--ineligible',
                    eligibility,
                    message: 'Client is not eligible for Digital Advice',
                  },
                }),
              ];
            }
          }),
        );
      }),
      catchError((error) => {
        return [
          fromRootStore.LogError({
            payload: {
              eventId: 'open-choice-effect--client-eligibility--error',
              message: `Error checking client's Digital Advice eligibility`,
              error,
            },
          }),
          fromRootStore.Next(),
        ];
      }),
    );
  });

  onboardingInitializeClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromRootStore.initializeOnboarding),
      exhaustMap(() =>
        this.store$.pipe(
          select(getMfePersonalAddressState),
          filter(
            (mfePersonalAddressState) =>
              mfePersonalAddressState.personalAddress.isClientAddressesLoaded,
          ),
          take(1),
          withLatestFrom(this.store$.select(selectOnboardingInitRequestBody)),
          switchMap(([, body]) =>
            this.digitalAdviceService.onboardInitClient(body).pipe(
              switchMap(() => [
                fromRootStore.LogInfo({
                  payload: { eventId: 'open-choice-effect--onboarding-initialization--success' },
                }),
                fromRootStore.Next(),
                fromRootStore.UpdateOnboardingInitialized({ payload: true }),
              ]),
            ),
          ),
        ),
      ),
      catchError((error) => [
        fromRootStore.LogError({
          payload: {
            eventId: 'open-choice-effect--onboarding-initialization--error',
            error,
          },
        }),
        fromRootStore.rejectDAOffer(),
        fromRootStore.UpdateDAComponentFailure({ payload: true }),
        fromRootStore.Go({ path: [`secure/minor-transfer/review`] }),
      ]),
    ),
  );

  rollbackOnboarding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(rollbackOnboarding),
      withLatestFrom(
        this.store$.select(selectOnboardingInitialized),
        this.store$.select(selectAcceptedDAOffer),
      ),
      switchMap(([action, onboardingInitialized, acceptedDAOffer]) => {
        if (onboardingInitialized && !acceptedDAOffer) {
          return this.digitalAdviceService.rollbackOffer().pipe(
            switchMap(() => {
              return of(
                fromRootStore.LogInfo({
                  payload: {
                    eventId: 'open-choice-effect--rollback-da-offer--success',
                    message: 'Rolled back DA offer',
                  },
                }),
              );
            }),
            catchError((error) => {
              return of(
                fromRootStore.LogError({
                  payload: {
                    eventId: 'open-choice-effect--rollback-da-offer--error',
                    message: 'Error occurred during RollBackDAOffer',
                    error,
                  },
                }),
              );
            }),
          );
        } else {
          return EMPTY;
        }
      }),
    ),
  );
}
